import React from 'react';
import cn from 'classnames';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import type { IconComponentType } from '@creditflowresearch/uilib/Icon';

import styles from './menuItem.module.scss';

export const MenuItemType = {
  button: 'button',
  navLink: 'nav-link',
  link: 'link',
};

type CommonProps = {
  children?: string | React.ReactNode,
  title?: string,
  LinkComponent?: React.ComponentType<any>,
  textClassName?: string,
  loading?: boolean,
  icon?: IconComponentType,
  iconWrapperClassName?: string,
  type?: typeof MenuItemType[keyof typeof MenuItemType],
  className?: string,
  disabled?: boolean,
  dataTest: string,
  onClick?: (event: any) => void,
  danger?: boolean,
  success?: boolean,
  to?: string,
  target?: string,
  href?: string,
  tooltipContent?: any,
  tooltipClassName?: string,
  tooltipWrapperClassName?: string,
};

export type IMenuItemProps = CommonProps;

const dataTestPrefix = 'dropdownItem';

const MenuItem = ({
  children,
  title,
  LinkComponent,
  textClassName,
  icon: Icon,
  className = '',
  iconWrapperClassName = '',
  disabled,
  success,
  loading,
  type = MenuItemType.button,
  dataTest,
  onClick,
  danger,
  to,
  target,
  href,
  tooltipContent,
  tooltipClassName,
  tooltipWrapperClassName,
}: IMenuItemProps): React.ReactElement => {
  const content = (
    <>
      { Icon && (
        <span className={ cn(styles.iconWrapper, iconWrapperClassName) }>
          { loading
            ? <Spinner className={ styles.spinner } color="currentColor" />
            : <Icon className={ styles.icon } /> }
        </span>
      ) }
      <span className={ cn(styles.text, textClassName) }>{ title || children }</span>
    </>
  );

  const renderItem = (): React.ReactElement => {
    if (type === MenuItemType.button) {
      return (
        <button
          onClick={ !loading ? onClick : undefined }
          type="button"
          role="menuitem"
          tabIndex={ -1 }
          className={ cn(styles.button, className, {
            [styles.danger]: danger,
            [styles.success]: success,
            [styles.disabled]: disabled,
          }) }
          disabled={ disabled }
          data-test={ `${ dataTestPrefix }-${ dataTest }Button` }
        >
          { content }
        </button>
      );
    }

    if (type === MenuItemType.navLink) {
      return (
        <LinkComponent
          to={ to }
          role="menuitem"
          tabIndex={ -1 }
          className={ cn(styles.link, className, {
            [styles.disabled]: disabled,
          }) }
          activeClassName={ styles.activeLink }
          data-test={ `${ dataTestPrefix }-${ dataTest }NavLink` }
          target={ target }
          onClick={ onClick }
        >
          { content }
        </LinkComponent>
      );
    }

    return (
      <a
        href={ href }
        role="menuitem"
        tabIndex={ -1 }
        className={ cn(styles.link, className, {
          [styles.disabled]: disabled,
        }) }
        target="_blank"
        data-test={ `${ dataTestPrefix }-${ dataTest }Link` }
        onClick={ onClick }
      >
        { content }
      </a>
    );
  };

  return (
    <Tooltip
      disabled={ !tooltipContent }
      content={ tooltipContent }
      containerClassName={ cn(styles.tooltipContainer, tooltipClassName) }
      wrpClassName={ cn(styles.tooltipWrapper, tooltipWrapperClassName) }
    >
      { renderItem() }
    </Tooltip>
  );
};

export default MenuItem;
